import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Table, Button } from 'react-bootstrap';
import ModalWrapper from 'components/global/ModalWrapper/ModalWrapper';
import CloseModalButton from 'components/global/Constant/CloseModalButton';
import Loader from '../../Loader';
import formatMoney from 'utils/formatMoney';
import api from 'api/api';
import $appSettings from 'signals/AppSettings.signal';

const fundTypeLabel = (code, options) => {
  if (!code) return '–';
  const opt = options?.find((o) => o.code === code);
  return opt ? `${code} – ${opt.label}` : code;
};

const exportToCSV = (programs, year, quarter, fundTypeOptions) => {
  if (!programs || programs.length === 0) return;

  // CSV Headers matching table.tsv requirements
  const headers = [
    'CalCAP Loan #',
    'Date Funded',
    'Lender',
    'Borrower',
    'Total Loan Amount',
    'Enrolled Amount',
    'Date of Loan',
    'Authority Premium',
    'Premium/Funding Type',
    'Loan Purpose',
    'Loan Purpose - Other',
    'Interest Rate',
    'Fund Type',
  ];

  // Collect all entries from all programs
  const allEntries = programs.flatMap((program) =>
    (program.entries ?? []).map((entry) => ({
      ...entry,
      programName: program.programName,
      programType: program.programType,
    }))
  );

  // Convert entries to CSV rows
  const rows = allEntries.map((entry) => {
    // Remove dashes from fund type label to avoid encoding issues
    const fundTypeRaw = fundTypeLabel(entry.fundTypeCode, fundTypeOptions);
    const fundType = fundTypeRaw.replace(/–/g, '-'); // Replace en-dash with hyphen

    return [
      entry.calcapLoanNumber || '',
      entry.dateFunded || entry.date || '',
      entry.lenderName || '',
      entry.borrowerName || '',
      entry.totalLoanAmount ? (entry.totalLoanAmount / 100).toFixed(2) : '',
      entry.enrolledAmount ? (entry.enrolledAmount / 100).toFixed(2) : '',
      entry.dateOfLoan || '',
      entry.authorityPremium ? Number(entry.authorityPremium).toFixed(2) : '', // Already in dollars, don't divide by 100
      fundType,
      entry.loanPurpose || '',
      entry.loanPurposeOther || '',
      entry.interestRate || '',
      entry.fundTypeCode || '',
    ];
  });

  // Build CSV content
  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
  ].join('\n');

  // Create and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `eot-breakdown-Q${quarter}-${year}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const EotCurrentQuarterBreakdownModal = ({ show, onHide, year, quarter, platformId, programFilter = null }) => {
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fundTypeOptions = $appSettings.value.constants?.DISBURSEMENT_FUND_TYPE_OPTIONS ?? [];

  const programsToShow =
    breakdown?.programs?.filter(
      (p) =>
        !programFilter ||
        (p.programName === programFilter.programName &&
          p.programType === programFilter.programType)
    ) ?? [];
  const displayGrandTotal =
    breakdown && (programFilter ? programsToShow.reduce((s, p) => s + (p.totalCents ?? 0), 0) : breakdown.grandTotalCents);

  useEffect(() => {
    if (!show || !year || !quarter || !platformId) {
      setBreakdown(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .get({
        path: '/reports/quarterly/eot-breakdown',
        options: {
          where: {
            year_quarter_platformId: {
              year: Number(year),
              quarter: Number(quarter),
              platformId,
            },
          },
        },
      })
      .then((data) => {
        if (!cancelled) {
          setBreakdown(data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'Failed to load breakdown');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, year, quarter, platformId]);

  const handleExport = () => {
    exportToCSV(programsToShow, year, quarter, fundTypeOptions);
  };

  return (
    <ModalWrapper show={show} onHide={onHide} size="lg">
      <Modal.Header className="d-flex align-items-center justify-content-between">
        <Modal.Title as="h5">
          Program Allocated Funds EOT
          {year && quarter ? ` – Q${quarter} ${year}` : ''}
        </Modal.Title>
        <div className="d-flex align-items-center">
          {!loading && !error && breakdown && programsToShow.length > 0 && (
            <Button
              size="sm"
              variant="outline-primary"
              onClick={handleExport}
              className="mr-2"
            >
              Export Details
            </Button>
          )}
          <CloseModalButton onCloseModal={onHide} />
        </div>
      </Modal.Header>
      <Modal.Body>
        {loading && <Loader message="Loading..." />}
        {error && <p className="text-danger">{error}</p>}
        {!loading && !error && breakdown && (
          <div className="overflow-auto">
            {programsToShow.map((program) => (
              <div key={program.programName} className="mb-4">
                <h6 className="mb-2">
                  {program.programName} ({program.programType})
                  <span className="text-muted font-weight-normal ml-2">
                    {(() => {
                      const entries = program.entries ?? [];
                      const applicationCount = new Set(
                        entries.map((e) => e.applicationId).filter((id) => id != null)
                      ).size;
                      const recordCount = entries.length;
                      return (
                        <>
                          {applicationCount} application{applicationCount === 1 ? '' : 's'}, {recordCount} record
                          {recordCount === 1 ? '' : 's'}
                        </>
                      );
                    })()}
                  </span>
                </h6>
                <Table size="sm" bordered>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>CalCAP ID#</th>
                      <th>Date</th>
                      {program.programType !== 'Equity' && <th>Fund Type</th>}
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...program.entries]
                      .sort((a, b) => (a.date || '').localeCompare(b.date || '') || (a.id - b.id))
                      .map((entry) => (
                      <tr key={`${entry.id}-${entry.applicationId || ''}-${entry.date || ''}`}>
                        <td>{entry.id}</td>
                        <td>
                          {entry.applicationId != null ? (
                            <Link
                              to={`/edo/portfolio/${entry.applicationId}?tab=disbursements`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {entry.calcapLoanNumber || entry.applicationId}
                            </Link>
                          ) : (
                            '–'
                          )}
                        </td>
                        <td>{entry.date ?? '–'}</td>
                        {program.programType !== 'Equity' && (
                          <td>{fundTypeLabel(entry.fundTypeCode, fundTypeOptions)}</td>
                        )}
                        <td className="text-right">{formatMoney(entry.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-weight-bold">
                      <td colSpan={program.programType === 'Equity' ? 3 : 4}>
                        Program total
                      </td>
                      <td className="text-right">{formatMoney(program.totalCents)}</td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            ))}
            {programsToShow.length > 0 && (
              <div className="mt-3 pt-3 border-top">
                <strong>Grand total: </strong>
                <span className="float-right">{formatMoney(displayGrandTotal)}</span>
              </div>
            )}
            {programsToShow.length === 0 && (
              <p className="text-muted">No EOT entries for this quarter.</p>
            )}
          </div>
        )}
      </Modal.Body>
    </ModalWrapper>
  );
};

export default EotCurrentQuarterBreakdownModal;
